import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff, KeyRound, ArrowLeft, Users, Inbox, BarChart3, Bot } from 'lucide-react';
import { fondoDeMarca } from '../components/lateral';
import api from '../utils/api';

/* Lo que el CRM le resuelve al equipo del cliente. Lo mismo que ve en el portal:
   primero para qué sirve, después el formulario. */
const BENEFICIOS = [
  { icono: Users, titulo: 'Todos tus contactos', texto: 'Cada consulta que entra, con su origen y en qué estado quedó.' },
  { icono: Inbox, titulo: 'Las conversaciones', texto: 'WhatsApp e Instagram en un solo lugar, sin pasarse el celular.' },
  { icono: Bot, titulo: 'El agente de IA', texto: 'Atiende al instante y te deja las consultas que valen la pena.' },
  { icono: BarChart3, titulo: 'Qué canal vende', texto: 'De dónde viene cada venta, para saber dónde invertir.' },
];

export default function Login() {
  const { login, abrirSesion, tenant } = useAuth();
  const [modo, setModo] = useState('password');     // password | pedir-codigo | codigo | olvide
  const [usuario, setUsuario] = useState('');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [password, setPassword] = useState('');
  const [verPass, setVerPass] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [cargando, setCargando] = useState(false);

  const esAdmin = window.location.hostname.startsWith('admin.');
  const ICONO = esAdmin ? '/icon-admin-192.png' : '/icon-192.png';
  const [marca, setMarca] = useState({ logo: null, nombre: esAdmin ? 'Administrador' : 'Udream App', color: null });
  useEffect(() => {
    if (!tenant) return;
    setMarca({
      logo: tenant.logo_url || null,
      nombre: tenant.company_name || tenant.name || 'Udream App',
      color: tenant.primary_color || null,
    });
  }, [tenant]);

  const fondo = useMemo(() => fondoDeMarca(marca.color), [marca.color]);

  const pedir = async (fn, alFallar) => {
    setError(''); setAviso(''); setCargando(true);
    try { await fn(); }
    catch (err) { setError(err?.response?.data?.error || alFallar); }
    finally { setCargando(false); }
  };

  const entrar = (e) => { e.preventDefault(); pedir(() => login(usuario, password), 'Usuario o contraseña incorrectos'); };

  const pedirCodigo = (e) => {
    e.preventDefault();
    pedir(async () => {
      await api.post('/auth/codigo', { email });
      setModo('codigo');
      setAviso('Si ese mail tiene acceso, en un minuto te llega un código de 6 dígitos.');
    }, 'No se pudo enviar el código');
  };

  const entrarConCodigo = (e) => {
    e.preventDefault();
    pedir(async () => {
      const { data } = await api.post('/auth/codigo/entrar', { email, codigo });
      abrirSesion(data);
    }, 'El código no es válido');
  };

  const olvide = (e) => {
    e.preventDefault();
    pedir(async () => {
      await api.post('/auth/olvide', { email });
      setAviso('Si ese mail tiene acceso, te mandamos un enlace para cambiar la contraseña. Vence en 2 horas.');
    }, 'No se pudo enviar el mail');
  };

  const mensajes = (
    <>
      {error && <div style={estilos.error}>{error}</div>}
      {aviso && <div style={estilos.aviso}>{aviso}</div>}
    </>
  );

  const volver = (
    <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 14, paddingLeft: 0 }}
      onClick={() => { setModo('password'); setError(''); setAviso(''); setCodigo(''); }}>
      <ArrowLeft size={14} /> Volver
    </button>
  );

  return (
    <div className="acceso">
      <div className="acceso-form">
        <div style={{ width: '100%', maxWidth: 380 }}>
          <img className="acceso-logo-chico" src={marca.logo || ICONO} alt="" onError={(e) => { e.target.src = ICONO; }}
            style={{ width: 44, height: 44, borderRadius: 13, objectFit: 'cover', marginBottom: 22 }} />

          {modo === 'password' && (
            <form onSubmit={entrar}>
              <h1 style={estilos.titulo}>Entrá a tu CRM</h1>
              <p style={estilos.bajada}>Tus contactos, tus conversaciones y tus campañas.</p>
              {mensajes}
              <div className="form-group">
                <label htmlFor="usuario">Usuario o email</label>
                <input id="usuario" value={usuario} onChange={e => setUsuario(e.target.value)}
                  autoComplete="username" autoFocus required />
              </div>
              <div className="form-group">
                <label htmlFor="password">Contraseña</label>
                <div style={{ position: 'relative' }}>
                  <input id="password" type={verPass ? 'text' : 'password'} value={password}
                    onChange={e => setPassword(e.target.value)} autoComplete="current-password" required
                    style={{ paddingRight: 42 }} />
                  <button type="button" onClick={() => setVerPass(!verPass)} style={estilos.ojo}
                    aria-label={verPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                    {verPass ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={cargando} style={{ width: '100%', justifyContent: 'center' }}>
                <LogIn size={16} /> {cargando ? 'Entrando...' : 'Entrar'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                onClick={() => { setModo('olvide'); setError(''); setAviso(''); }}>
                Olvidé mi contraseña
              </button>

              <div className="separador-o"><span>o</span></div>
              <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setModo('pedir-codigo'); setError(''); setAviso(''); }}>
                <KeyRound size={15} /> Entrar con un código al mail
              </button>
              <p style={estilos.nota}>Si no recordás tu contraseña, esta es la forma más rápida</p>
            </form>
          )}

          {modo === 'pedir-codigo' && (
            <form onSubmit={pedirCodigo}>
              <h1 style={estilos.titulo}>Entrar con un código</h1>
              <p style={estilos.bajada}>Te mandamos un código de 6 dígitos al mail de tu cuenta.</p>
              {mensajes}
              <div className="form-group">
                <label htmlFor="email-codigo">Email</label>
                <input id="email-codigo" type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
              </div>
              <button type="submit" className="btn btn-primary" disabled={cargando} style={{ width: '100%', justifyContent: 'center' }}>
                {cargando ? 'Enviando...' : 'Enviarme el código'}
              </button>
              {volver}
            </form>
          )}

          {modo === 'codigo' && (
            <form onSubmit={entrarConCodigo}>
              <h1 style={estilos.titulo}>Revisá tu mail</h1>
              <p style={estilos.bajada}>Escribí el código que te llegó. Vence en 15 minutos.</p>
              {mensajes}
              <div className="form-group">
                <label htmlFor="codigo">Código</label>
                <input id="codigo" value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric" placeholder="000000" autoFocus required
                  style={{ fontSize: 24, letterSpacing: 10, textAlign: 'center', fontWeight: 700 }} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={cargando || codigo.length < 6}
                style={{ width: '100%', justifyContent: 'center' }}>
                {cargando ? 'Entrando...' : 'Entrar'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                onClick={() => { setModo('pedir-codigo'); setCodigo(''); setError(''); }}>
                No me llegó, mandar otro
              </button>
              {volver}
            </form>
          )}

          {modo === 'olvide' && (
            <form onSubmit={olvide}>
              <h1 style={estilos.titulo}>Recuperar tu contraseña</h1>
              <p style={estilos.bajada}>Te mandamos un enlace al mail de tu cuenta para que elijas una nueva.</p>
              {mensajes}
              <div className="form-group">
                <label htmlFor="email-olvide">Email</label>
                <input id="email-olvide" type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
              </div>
              <button type="submit" className="btn btn-primary" disabled={cargando} style={{ width: '100%', justifyContent: 'center' }}>
                {cargando ? 'Enviando...' : 'Enviarme el enlace'}
              </button>
              {volver}
            </form>
          )}

          <div style={estilos.firma}>{tenant ? `${marca.nombre} · Udream App` : 'Udream App'}</div>
        </div>
      </div>

      <div className="acceso-marca" style={fondo}>
        <div style={{ maxWidth: 420 }}>
          <img src={marca.logo || ICONO} alt="" onError={(e) => { e.target.src = ICONO; }}
            style={{ width: 62, height: 62, borderRadius: 17, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.4)' }} />
          <div style={{ fontSize: 11, letterSpacing: 2.4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)', fontWeight: 700, margin: '22px 0 10px' }}>
            {tenant ? marca.nombre : 'Udream App'}
          </div>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.18, letterSpacing: -0.6, marginBottom: 28 }}>
            Todo tu trabajo comercial,{' '}<br />en un solo lugar
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {BENEFICIOS.map(b => (
              <div key={b.titulo} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <span style={estilos.chapa}><b.icono size={17} /></span>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{b.titulo}</div>
                  <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 12.5, marginTop: 2, lineHeight: 1.5 }}>{b.texto}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const estilos = {
  titulo: { fontSize: 25, fontWeight: 800, letterSpacing: -0.5, marginBottom: 5 },
  bajada: { fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.55 },
  error: { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.32)', color: '#ef4444',
    padding: '10px 12px', borderRadius: 10, fontSize: 13, marginBottom: 14 },
  aviso: { background: 'var(--accent-glow)', border: '1px solid var(--accent)', color: 'var(--text-secondary)',
    padding: '10px 12px', borderRadius: 10, fontSize: 13, marginBottom: 14, lineHeight: 1.5 },
  ojo: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none',
    border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 2 },
  nota: { fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 },
  firma: { fontSize: 11.5, color: 'var(--text-muted)', marginTop: 26 },
  chapa: { width: 34, height: 34, borderRadius: 11, flexShrink: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: 'rgba(255,255,255,0.14)', color: '#fff' },
};
